import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { BasicInformation } from '@/components/patient/BasicInformation';
import { ContactInformation } from '@/components/patient/ContactInformation';
import { MedicalInformation } from '@/components/patient/MedicalInformation';
import { DocumentsList } from '@/components/patient/DocumentsList';
import { FamilyLinks } from '@/components/patient/FamilyLinks';
import { PatientLoyaltyCard } from '@/components/patient/PatientLoyaltyCard';
import { usePatientForm } from '@/components/patient/usePatientForm';
import { useAuth } from '@/contexts/AuthContext';
import { patientApi } from '@/api/patient';
import { toast } from 'sonner';
import { Download, Loader2 } from 'lucide-react';

export default function PatientDetails({ patientId }: { patientId: string }) {
    const { user } = useAuth();
    const [historyDownloading, setHistoryDownloading] = useState(false);
    const {
        formData,
        isEditing,
        setIsEditing,
        handleInputChange,
        handleSelectChange,
        handleSubmit,
        isLoading,
        error
    } = usePatientForm(patientId);

    const backendBaseUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3000';

    const canDownloadFullHistory =
        user?.role === 'HOSPITAL_ADMIN' || user?.role === 'SUPER_ADMIN';

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-lg">Loading patient details...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-lg text-red-500">Error loading patient details. Please try again later.</div>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-6 max-w-4xl">
            <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
                <div className="flex items-center gap-4">
                    <p className='font-bold text-2xl'>Patient Details</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {canDownloadFullHistory && (
                        <Button
                            type="button"
                            variant="secondary"
                            disabled={historyDownloading}
                            onClick={async () => {
                                setHistoryDownloading(true);
                                try {
                                    const res = await patientApi.downloadFullPatientHistory(patientId, 'zip');
                                    const blob = new Blob([res.data], { type: 'application/zip' });
                                    const url = window.URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    const safe =
                                        (formData.uhid || formData.patientUniqueId || patientId).replace(
                                            /[^a-zA-Z0-9._-]+/g,
                                            '_'
                                        );
                                    a.download = `patient-history-${safe}-csv.zip`;
                                    document.body.appendChild(a);
                                    a.click();
                                    a.remove();
                                    window.URL.revokeObjectURL(url);
                                    toast.success('CSV bundle downloaded (ZIP)');
                                } catch {
                                    toast.error('Could not download patient history');
                                } finally {
                                    setHistoryDownloading(false);
                                }
                            }}
                        >
                            {historyDownloading ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                                <Download className="h-4 w-4 mr-2" />
                            )}
                            Download history (CSV zip)
                        </Button>
                    )}
                    {canDownloadFullHistory && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground"
                            disabled={historyDownloading}
                            onClick={async () => {
                                setHistoryDownloading(true);
                                try {
                                    const res = await patientApi.downloadFullPatientHistory(patientId, 'json');
                                    const blob = new Blob([res.data], { type: 'application/json;charset=utf-8' });
                                    const url = window.URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    const safe =
                                        (formData.uhid || formData.patientUniqueId || patientId).replace(
                                            /[^a-zA-Z0-9._-]+/g,
                                            '_'
                                        );
                                    a.download = `patient-history-${safe}.json`;
                                    document.body.appendChild(a);
                                    a.click();
                                    a.remove();
                                    window.URL.revokeObjectURL(url);
                                    toast.success('JSON export downloaded');
                                } catch {
                                    toast.error('Could not download JSON export');
                                } finally {
                                    setHistoryDownloading(false);
                                }
                            }}
                        >
                            JSON
                        </Button>
                    )}
                    {!isEditing ? (
                        <Button onClick={() => setIsEditing(true)}>Edit Details</Button>
                    ) : (
                        <>
                            <Button variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
                            <Button onClick={handleSubmit}>Save Updates</Button>
                        </>
                    )}
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
                <BasicInformation
                    formData={formData}
                    isEditing={isEditing}
                    onInputChange={handleInputChange}
                    onSelectChange={handleSelectChange}
                />

                <ContactInformation
                    formData={formData}
                    isEditing={isEditing}
                    onInputChange={handleInputChange}
                    onSelectChange={handleSelectChange}
                />

                <MedicalInformation
                    formData={formData}
                    isEditing={isEditing}
                    onInputChange={handleInputChange}
                    onSelectChange={handleSelectChange}
                />

                <DocumentsList
                    backendBaseUrl={backendBaseUrl}
                    patientId={patientId}
                />

                <FamilyLinks
                    patientId={patientId}
                />

                <PatientLoyaltyCard patientId={patientId} />
            </form>
        </div>
    );
}

